import type {
  SupplyAveragePrice,
  SupplyPricePeriod,
} from '@obraplan/core/application/ports/requisition';
import { sameUnit } from '@obraplan/core/domain/takeoff/material-price';
import { mesmoCodigo } from './codigo-de-compras';
import { precoUnitario } from './formato';

/**
 * O que a tela diz do compras ao informar o código de um item (Fase 29, etapa 1).
 *
 * **Nada daqui é guardado.** O preço médio aparece para quem escolhe o código conferir que
 * escolheu o produto certo — o tijolo de R$ 0,85, e não o de R$ 8,50 —, e a unidade, para
 * avisar desde já que o preço vai precisar de um fator. Quem guarda é a etapa 2: aplicado o
 * código conferido, a tela tira a foto do compras (D5), e o fator e o preço do material na
 * obra moram no diálogo do preço (D6, RN-397 — `precos-da-obra.ts`).
 */

/** O rótulo da linha do preço médio — o período vai junto, porque média sem janela não diz nada. */
const ROTULO_DA_MEDIA = 'Preço médio no compras (12 meses)';

const doisDigitos = (valor: number): string => String(valor).padStart(2, '0');

/** `AAAA-MM-DD` pelos campos **locais** da data — o dia de quem olha, e não o de Greenwich. */
function dataDeNegocio(ano: number, mes: number, dia: number): string {
  return `${String(ano).padStart(4, '0')}-${doisDigitos(mes + 1)}-${doisDigitos(dia)}`;
}

/**
 * Os doze meses até hoje, as duas pontas incluídas: o padrão do período (D4).
 *
 * Pelo fuso do aparelho, e não por `toISOString`: às 22h de Brasília já é amanhã em UTC, e o
 * período andaria um dia para a frente à noite. O 29 de fevereiro vira 28 no ano anterior,
 * em vez de escorregar para 1º de março e encurtar a janela.
 */
export function periodoDeDozeMeses(hoje: Date): SupplyPricePeriod {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const dia = hoje.getDate();
  const ultimoDiaDoMes = new Date(ano - 1, mes + 1, 0).getDate();
  return {
    from: dataDeNegocio(ano - 1, mes, Math.min(dia, ultimoDiaDoMes)),
    to: dataDeNegocio(ano, mes, dia),
  };
}

const vezes = (quantas: number, uma: string, varias: string): string =>
  `${String(quantas)} ${quantas === 1 ? uma : varias}`;

/**
 * A linha do preço médio, como a tela a diz — ou nada.
 *
 * - **lista vazia** é "não há integração" (`GetSupplyAveragePrices`): não há o que dizer, e
 *   dizer "sem preço" acusaria um problema que não existe;
 * - `SEM_MEDIA` é o produto que existe e não teve compra nem cotação no período — não é zero
 *   (RN-393);
 * - `NAO_ENCONTRADO` é o código que o compras não conhece, ou inativo (RN-394). O SKU que não
 *   voltou na resposta é dito do mesmo jeito: a porta promete um resultado por SKU, e o que
 *   falta não é preço.
 *
 * O preço com até quatro casas (D17), e a conta de onde ele saiu: três compras dizem mais
 * que uma, e quem escolhe o código merece saber em quantos pontos a média se apoia.
 */
export function textoDoPrecoMedio(
  medias: readonly SupplyAveragePrice[],
  code: string,
): string | undefined {
  if (medias.length === 0) return undefined;
  const media = medias.find((candidata) => mesmoCodigo(candidata.code, code));
  if (media === undefined || media.status === 'NAO_ENCONTRADO') {
    return `${ROTULO_DA_MEDIA}: não encontrado no compras`;
  }
  if (media.status === 'SEM_MEDIA') {
    return `${ROTULO_DA_MEDIA}: sem compra nem cotação no período`;
  }
  return `${ROTULO_DA_MEDIA}: ${precoUnitario(media.average)} · ${vezes(
    media.purchases,
    'compra',
    'compras',
  )}, ${vezes(media.quotations, 'cotação', 'cotações')}`;
}

/** A unidade do produto no compras, quando a resposta da média a traz. */
export function unidadeNaMedia(
  medias: readonly SupplyAveragePrice[],
  code: string,
): string | undefined {
  const media = medias.find((candidata) => mesmoCodigo(candidata.code, code));
  return media === undefined || media.status === 'NAO_ENCONTRADO' ? undefined : media.unit;
}

/** A unidade do compras ao lado da do item, e se elas diferem (D6). */
export interface ComparacaoDeUnidade {
  readonly texto: string;
  readonly diferente: boolean;
}

/**
 * A unidade do compras contra a do item (D6) — só aviso, e a do item não muda.
 *
 * O preço do compras é por **unidade do compras**: R$ 0,85 a unidade de tijolo não é o preço
 * do milheiro, e o quilo de aço não é o preço da barra. Unidade diferente pede um fator no
 * material ("1 UN = 1,85 kg"), informado no preço dele (etapa 2); sem fator, o médio não vale
 * e o material fica sem preço, ou no provisório (D6). O aviso diz a pergunta que vai faltar
 * responder, já com as duas unidades no lugar.
 *
 * A comparação é a do domínio (`sameUnit`): "UN", "UNI", "UND" e "UNID" são a mesma unidade, e
 * "M³" é "M3" (confirmado em 13/10/2026); "UN" e "KG" continuam diferentes.
 */
export function compararUnidades(
  doCompras: string | undefined,
  doItem: string | undefined,
): ComparacaoDeUnidade | undefined {
  if (doCompras === undefined || doCompras.trim() === '') return undefined;
  const compras = doCompras.trim();
  if (doItem === undefined || doItem.trim() === '') {
    return { texto: `compras em ${compras}; o item não tem unidade`, diferente: false };
  }
  const item = doItem.trim();
  if (sameUnit(compras, item)) {
    return { texto: `mesma unidade: compras e item em ${item}`, diferente: false };
  }
  return {
    texto: `unidade diferente: compras em ${compras}, item em ${item} — o preço pede o fator: 1 ${item} = quantos ${compras}?`,
    diferente: true,
  };
}
